import i18next from 'i18next'
import React from 'react'
import { ButtonGhost } from '../../../../utils/buttons'
import { SVGIcon } from '../../../../utils/svgicon'
import { TooltipLayout } from '../../../../utils/tooltip'
import { Form } from 'react-bootstrap'
import { getLanguage } from '../../../../utils/localization'
import { AmountConverterState } from './componentDetailState'

export type AmountConverterInputType = 'preset' | 'up' | 'down' | 'text'

export const AmountConverter = (props: {
  amountConverterState: AmountConverterState
  multiplierMinimum: number
  multiplierMaximum: number
  onChangeMultiplier: (multiplier: number | undefined, inputText: string, inputType: AmountConverterInputType) => void
}) => {
  const {
    amountConverterState,
    multiplierMinimum,
    multiplierMaximum,
    onChangeMultiplier
  } = props

  const clamp = (number: number, min: number, max: number) => {
    return Math.max(min, Math.min(number, max));
  }

  const calculateNextValue = (currentValue: number, direction: 'up' | 'down') => {
    let result = currentValue;
    if (direction === 'up') {
      const step = currentValue < 1 ? 0.25 : 1;

      // Use a different minimum of 0.25 here, since we do not want to step down to 0
      result = clamp(currentValue + step, 0.25, multiplierMaximum);
    } else {
      const step = currentValue <= 1 ? 0.25 : 1;
      result = clamp(currentValue - step, 0.25, multiplierMaximum);
    }

    // Convert to string by rounding to 2 decimals and convert back to number
    // To prevent floating point errors accumulating after many steps up/down
    return +result.toFixed(2);
  }

  return (
    <div className="block__section hide__print">
      <h2 className="block__subtitle block__subtitle--tooltip title--xs">
        {i18next.t('component:amountConverterTitle')}

        <TooltipLayout
          id="amount-converter-tooltip"
          tooltipCopy={i18next.t('component:amountConverterTooltip')}
        >
        <SVGIcon iconId="info-circle" size="20" />
        </TooltipLayout>
      </h2>

      <div className="amount-converter">
        <AmountConverterStepButton
          label='-'
          onClick={() => {
            // Do not loop back to 1 if clicking the step down button results in the 0 value
            // We need to explicitly check for 0 here since the 0 value is not 'truthy', but in case of undefined/null/NaN, we want to fallback to 1
            const newValue = amountConverterState.multiplier || amountConverterState.multiplier === 0 ? calculateNextValue(amountConverterState.multiplier, 'down') : 1;
            onChangeMultiplier(newValue, newValue.toString(), 'down')
          }}
        />

        <AmountConverterInputField
          min={multiplierMinimum}
          max={multiplierMaximum}
          value={amountConverterState.inputText}
          multiplier={amountConverterState.multiplier}
          calculateStep={calculateNextValue}
          onChangeMultiplier={onChangeMultiplier}
        />

        <AmountConverterStepButton
          label='+'
          onClick={() => {
            const newValue = amountConverterState.multiplier ? calculateNextValue(amountConverterState.multiplier, 'up') : 1;
            onChangeMultiplier(newValue, newValue.toString(), 'up')
          }}
        />

        <AmountConverterPresetButton
          multiplier={0.5}
          label={getLanguage() === 'nl' ? '0,5x' : '0.5x'}
          onClick={onChangeMultiplier}
        />

        <AmountConverterPresetButton
          multiplier={1}
          label='1x'
          onClick={onChangeMultiplier}
        />

        <AmountConverterPresetButton
          multiplier={2}
          label='2x'
          onClick={onChangeMultiplier}
        />

        <AmountConverterPresetButton
          multiplier={4}
          label='4x'
          onClick={onChangeMultiplier}
        />
      </div>
    </div>
  )
}

const AmountConverterPresetButton = (props: {
  multiplier: number
  label: string
  onClick: (multiplier: number, inputText: string, inputType: AmountConverterInputType) => void
}) => (
  <ButtonGhost
    kind="button"
    onClick={() => props.onClick(props.multiplier, props.multiplier.toString(), 'preset')}
    size="s"
    label={props.label}
    title={props.label}
    extraClasses="block__button amount-converter__preset"
  />
)

const AmountConverterStepButton = (props: {
  label: string
  onClick: () => void
}) => (
  <ButtonGhost
    kind="button"
    onClick={props.onClick}
    size="s"
    label={props.label}
    title={props.label}
    extraClasses="block__button amount-converter__incremental"
  />
)

const AmountConverterInputField = (props: {
  value: string
  multiplier: number | undefined
  min: number
  max: number
  calculateStep: (currentValue: number, direction: 'up' | 'down') => number
  onChangeMultiplier: (multiplier: number | undefined, inputText: string, inputType: AmountConverterInputType) => void
}) => {

  const countDecimals = (input: number) => {
    // If round down the value is equal to its own value, there are no decimals
    if (Math.floor(input) === input)
      return 0;

    return input.toString().split(".")[1].length || 0;
  }

  const limitTwoDecimals = (input: string) => {
    const value = Number(input.replace(',', '.'));

    if (!value)
      return input;

    if (countDecimals(value) > 2) {
      // Instead of calculating/rounding based on the number value, we do string manupilation here
      // so that we can retain the comma or dot that was used as decimal separator instead of always overriding it with a dot
      let decimalIndex = value.toString().lastIndexOf(".");

      if (decimalIndex < 0)
        return input;

      let inputUntilDecimal = decimalIndex > 0 ? input.substring(0, decimalIndex + 1) : '';
      let inputAfterDecimal = input.substring(decimalIndex + 1, decimalIndex + 3);

      return inputUntilDecimal + inputAfterDecimal
    }

    return input;
  }

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputText = e.currentTarget.value.trim()

    // If the new value is not a number, basically ignore
    if (!inputText || Number.isNaN(inputText))
      return props.onChangeMultiplier(undefined, inputText, 'text');

    // Make sure that dot or comma both work as decimal separator
    let newValue = Number(inputText.replace(',', '.'));

    if (newValue > props.max) {
      return props.onChangeMultiplier(props.max, props.max.toString(), 'text');
    }

    if (newValue < props.min) {
      return props.onChangeMultiplier(props.min, props.min.toString(), 'text');
    }

    // Allow maximum of 2 digits after decimal, otherwise trim the end after 2 digits
    if (countDecimals(newValue) > 2) {
      inputText = limitTwoDecimals(inputText);
      newValue = Number(inputText.replace(',', '.'))
    }

    // We cannot calculate the difference if the old value was not a valid value, so return the new value as-is
    if (!props.multiplier) {
      return props.onChangeMultiplier(newValue, inputText, 'text');
    }

    const oldValue = props.multiplier

    const valueDifference = newValue - oldValue;

    // If the difference between old and new is 0.25, the value was stepped up/down by the input control
    // In that case, we want to override the stepsize to provide a dynamic step size based on the current value
    // (step size = 1 when value > 1, otherwise stepsize 0.25)
    // When the difference was not 0.25, we assume the change was caused by a directly entered input text
    const isStepped = Math.abs(valueDifference) === 0.25;
    const nextValue = isStepped ? props.calculateStep(oldValue, valueDifference > 0 ? 'up' : 'down') : newValue;

    return props.onChangeMultiplier(nextValue, inputText, isStepped ? valueDifference > 0 ? 'up' : 'down' : 'text');
  }

  return (
    <Form.Control
      type='text'
      inputMode='decimal'
      pattern='^(([0]?)|([1-9]*[0-9]?))([,.]?[0-9]{0,2})?$' // Only allow decimal numbers with max 2 digits after decimal and no leading 0s
      value={props.value}
      style={{ minHeight: 'var(--height--button--s)' }} // Sergio: A bit of a hack to get the height of the input to line up with the preset buttons
      onChange={onInputChange}
      className='amount-converter__input'
    />
  )
}